---
title: From conversations to conversion, how cCommerce is revolutionizing sales
author: Ramy Assaf
date: '2022-02-15'
meta_description: cCommerce or connected commerce is revolutionizing sales by allowing businesses to close sales on social and messaging apps.
description: Online sales in MENA are expected to exceed $50 billion by the end of this year, making this region one of the most exciting emerging markets. The impact of the pandemic, digital transformation initiatives by brands and improvements in logistics and last mile delivery have vastly contributed to double-digit growth. Saudi Arabia is leading the way in terms of regional e-commerce growth (39 per cent CAGR) just ahead of UAE (38 per cent CAGR). However, a less talked about phenomenon driving this seismic shift is the rapidly changing consumer behaviors and attitudes towards retail in an omnichannel world.
tag: [commerce, cCommerce, online business, social media, social selling]
published: true
image: /images/blog/how-ccommerce-is-revolutionizing-sales.jpg
hasDetailPage: true
---

**_MENA’s sales and social footprint_**

Online sales in MENA are expected to exceed $50 billion by the end of this
year, making this region one of the most exciting emerging markets. The
impact of the pandemic, digital transformation initiatives by brands and
improvements in logistics and last mile delivery have vastly contributed to
double-digit growth. Saudi Arabia is leading the way in terms of regional
e-commerce growth (39 per cent CAGR) just ahead of UAE (38 per cent CAGR).
However, a less talked about phenomenon driving this seismic shift is the
rapidly changing consumer behaviors and attitudes towards retail in an
omnichannel world.

MENA is home to some of the most prolific users of social media and
messaging apps in the world with an average of 3.5 hours spent on social
media per day. The UAE has the highest number of accounts per person
globally, at an average of 10.5 accounts per user. Not only are users in the
region creating and consuming content, but increasingly using these
platforms to discover and engage with brands. As of 2021, globally there
were more than 200 million business accounts on Instagram. Messaging apps
like Whatsapp, one of the most used platforms in the world with over 2
billion active users, has over 18 million business accounts where users send
approximately 175 million messages every day.

**_The missed opportunity_**

Savvy brands and online retailers are taking notice of how their customers
are discovering, interacting, and shopping with them, yet traditional
eCommerce is proving to be even more challenging. Online retailers are
finding it harder to get discovered (increasing digital media costs),
witnessing high abandoned cart rates and struggling with decreasing customer
lifetime value through traditional eCommerce. These are just a few of the
reasons why there is increasing pressure on brands to meet their bottom
lines and look for an advantage amongst their competitors. In addition, the
low to almost zero barriers to entry for digitally native challenger brands
and small to medium businesses have further compounded these pressures and
are changing the rules of the game. This widening gap between where and how
customers meet, interact and shop with brands is paving the way for the next
generation of commerce.

**_Say hello to cCommerce_**

cCommerce is a way to buy products and services through social media and
messaging apps. Research has shown that almost 87 per cent of people feel
more connected to brands that communicate through social and messaging apps.
Facebook’s research found that 53 per cent of respondents said they were
more likely to buy from a company and take their advice through messaging
apps like WhatsApp or Facebook Messenger. A message sent by a brand to its
customer is opened 98 per cent of the time on a messaging app versus an
average 10 per cent open rate for an email or branded newsletter.

With conversational commerce, it is possible to reach potential and existing
customers directly to build trusted and personal relationships through
conversations that often lead to new interactions, effective personalized
recommendations and increased spend. This behavior has quickly become a
worldwide trend, allowing for brands to interact with shoppers in new ways.
Brands that are pioneering cCommerce have found that when compared to
traditional online carts, the average cCommerce cart is 2.7 times bigger and
is 20 times more likely to convert into a sale.

Brands can now use messaging apps to chat with their customers allowing them
to discover products, make purchases, avail offers, get personalized
recommendations as well as benefit from customer support. Imagine
experiencing the warmth of a knowledgeable salesperson anywhere, anytime on
any device. As a result, cCommerce is poised to change the way customers
shop and communicate with brands.

**_What does this mean for retailers and brands_**

Steve Jobs once said “Get closer to your customers. So close, in fact, that
you tell them what they need before they realize it themselves.” This could
not be truer in today’s world of big data. cCommerce platforms like Zbooni
and its tools help brands build a comprehensive picture of their customer by
tracking over three million data points. Past order history, product
preferences, customer profile, frequently asked questions and sales data can
help retailers create bespoke offers and product recommendations.

cCommerce acts as a complementary sales channel for brands seeking to engage
with their customers who are increasingly wanting a more personalized
digital experience. Retailers can use the power of chat over habitual
digital channels like WhatsApp to share new product releases, sales and
offers or brand content and reviews to turn conversations into conversions.
Businesses that use Zbooni have witnessed a phenomenal 81 per cent
conversion rate due to this personalized approach in selling.

For larger retailers with brick-and-mortar outlets, cCommerce can also prove
to be a useful tool to increase sales and bridge the gap from offline to
online or vice versa. Sales representatives can provide concierge-like
services over messaging apps to their loyal customer database and notify
them of exclusive sales, new products, and support with customer queries.
This helps brick and mortar retail maximize their sales staff’s time by
increasing productivity and selling more. As brands seek to enhance
omnichannel strategies and reconfigure brick and mortar operations largely
affected by the pandemic and shifting in-store experience expectations,
cCommerce offers an innovative advantage.

**_About Zbooni_**

Zbooni is pioneering cCommerce in the MENA region, and since the launch of
its app in 2018 has helped thousands of businesses to interact, engage and
sell more effectively, powering transactions for hundreds of thousands of
customers and generating multimillion dollar sales revenue every month. Its
expert team is based across offices in United Arab Emirates, Saudi Arabia,
Jordan and Egypt, scaling rapidly to meet the growing needs of the cCommerce
revolution. The Zbooni platform provides tools and solutions that empower
businesses, both big and small, to capture orders, accept payments, sell
more, and track every order. To find out more about Zbooni, visit zbooni.com
or download the app.
